import { CheckCircle, MessageSquare, Sparkles } from 'lucide-react'
import { SiGoogleads } from 'react-icons/si'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import {
  googleAdsCalloutManagement,
  type GoogleAdsCalloutManagementResult,
} from 'sprites-types/workflow/tools/googleAdsCalloutManagement'
import { SchemaValidationError } from '../shared/safeParseToolResult'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'

function GoogleAdsCalloutManagementDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(googleAdsCalloutManagement, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data as GoogleAdsCalloutManagementResult
  const { linkTarget, calloutId } = result

  // Get input data from nodeExecution
  const inputs = nodeExecution.inputs as any
  const calloutText = inputs?.functionArgs?.action?.params?.calloutText || ''
  const targetId =
    inputs?.functionArgs?.action?.params?.campaignId ||
    inputs?.functionArgs?.action?.params?.assetGroupId ||
    inputs?.functionArgs?.action?.params?.adGroupId ||
    ''

  const targetType =
    linkTarget === 'assetGroup' ? 'Asset Group' : linkTarget === 'adGroup' ? 'Ad Group' : 'Campaign'

  return (
    <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
      <div className="flex items-start gap-4 px-4 py-3.5">
        <div className="mt-0.5 flex-shrink-0">
          <div className="rounded-full bg-gradient-to-br from-purple-500 to-purple-600 p-2.5 shadow-lg">
            <MessageSquare className="h-5 w-5 text-white" />
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-3 flex items-center gap-2">
            <SiGoogleads className="h-4 w-4 text-[#4285f4]" />
            <div className="text-sm font-semibold text-gray-900">Callout Extension Added</div>
            <div className="ml-auto">
              <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                Active
              </span>
            </div>
          </div>

          {/* Preview Card */}
          <div className="mb-3 rounded-xl border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-white p-3 shadow-sm">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 flex-shrink-0 text-purple-600" />
              <div className="inline-flex items-center rounded-full bg-purple-100 px-3 py-1.5">
                <span className="text-sm font-semibold text-purple-900">{calloutText}</span>
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-600">
              This callout will appear alongside your ad to highlight key features or benefits
            </div>
          </div>

          {/* Metadata */}
          <div className="rounded-lg bg-gray-50 px-3 py-2">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="font-medium text-gray-500">Target:</span>
                <span className="ml-1.5 text-gray-900">{targetType}</span>
              </div>
              <div>
                <span className="font-medium text-gray-500">ID:</span>
                <span className="ml-1.5 font-mono text-gray-900">{targetId}</span>
              </div>
              <div className="col-span-2">
                <span className="font-medium text-gray-500">Callout ID:</span>
                <span className="ml-1.5 font-mono text-gray-900">{calloutId}</span>
              </div>
            </div>
          </div>

          <div className="mt-2 flex items-center gap-1.5 text-xs text-gray-600">
            <CheckCircle className="h-3.5 w-3.5 text-green-600" />
            <span>Extension is live and will appear with your ads</span>
          </div>
        </div>
      </div>
    </div>
  )
}

function downloadGoogleAdsCalloutManagement(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function googleAdsCalloutManagementToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(googleAdsCalloutManagement, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data as GoogleAdsCalloutManagementResult
  const { linkTarget, calloutId } = result

  // Get input data from nodeExecution
  const inputs = nodeExecution.inputs as any
  const calloutText = inputs?.functionArgs?.action?.params?.calloutText || ''
  const targetId =
    inputs?.functionArgs?.action?.params?.campaignId ||
    inputs?.functionArgs?.action?.params?.assetGroupId ||
    inputs?.functionArgs?.action?.params?.adGroupId ||
    ''

  const targetType =
    linkTarget === 'assetGroup' ? 'Asset Group' : linkTarget === 'adGroup' ? 'Ad Group' : 'Campaign'

  return `## Google Ads Callout Added

**Callout Text:** ${calloutText}
**${targetType} ID:** ${targetId}
**Callout ID:** ${calloutId}

---
`
}

export const GoogleAdsCalloutManagementDisplay = createWorkflowToolComponent(
  GoogleAdsCalloutManagementDisplayComponent,
  downloadGoogleAdsCalloutManagement,
  googleAdsCalloutManagementToMarkdown
)
